'use client'

import { useMemo } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod/v4'

import type { AvatarList } from '@helper/utils/domain'
import type { Locale } from '@helper/utils/types'
import { UpdateAddressSchema, UpdateOrganizationSchema } from '@package/drizzle/schema'
import {
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@ui/web/components'
import { AvatarSelector, ProfileUpload, typographyVariants } from '@ui/web/lib'
import { tryCatch } from '@helper/utils/core'
import { isOtherCountry } from '@helper/utils/domain'
import cn from '@config/tailwind/cn'

/** Mirrors the Input control so the address comboboxes line up with the plain text fields. */
const ADDRESS_TRIGGER_CLASSNAME = cn(
  typographyVariants({ variant: 'body-lg' }),
  'srd:border-outline srd:bg-neutral-white srd:text-neutral-black srd:h-12 srd:w-full srd:min-w-0 srd:justify-between srd:rounded-lg srd:px-2.5 srd:py-1 srd:text-left srd:font-normal',
)

/** Fields backed by the Indonesian administrative dataset — meaningless for any other country. */
const INDONESIA_ONLY_ADDRESS_KEYS = ['province', 'regency', 'district', 'village', 'postalCode'] as const

const ADDRESS_REQUIRED_WHEN_PARTIAL = (locale: Locale) =>
  [
    { key: 'addressLineOne' as const, msg: 'Alamat harus diisi' },
    { key: 'province' as const, msg: locale === 'id' ? 'Provinsi harus diisi' : 'Province is required' },
    { key: 'regency' as const, msg: locale === 'id' ? 'Kota harus diisi' : 'City is required' },
    { key: 'district' as const, msg: locale === 'id' ? 'Kecamatan harus diisi' : 'District is required' },
    { key: 'village' as const, msg: locale === 'id' ? 'Kelurahan harus diisi' : 'Village is required' },
    { key: 'country' as const, msg: locale === 'id' ? 'Negara harus diisi' : 'Country is required' },
    { key: 'postalCode' as const, msg: locale === 'id' ? 'Kode pos harus diisi' : 'Postal code is required' },
  ] as const

// Base form schema: UpdateOrganizationSchema (name, level, npsn, logo, entityName) + UpdateAddressSchema + logoFile
const OrganizationDetailsFormBaseSchema = (locale: Locale) =>
  UpdateOrganizationSchema.pick({
    name: true,
    level: true,
    npsn: true,
    logo: true,
    entityName: true,
  })
    .extend({
      logoFile: z.instanceof(File).nullable(),
      address: UpdateAddressSchema.optional().default({}),
    })
    .superRefine((data, ctx) => {
      if (!data.name) {
        ctx.addIssue({
          code: 'custom',
          message: locale === 'id' ? 'Nama harus diisi' : 'Name is required',
          path: ['name'],
        })
      }
    })

// Schema with address validation: if any address field is present, all required fields must be present
const OrganizationDetailsFormSchemaWithAddressValidation = (locale: Locale) =>
  OrganizationDetailsFormBaseSchema(locale).superRefine((data, ctx) => {
    const addr = data.address
    if (typeof addr === 'object') {
      // Outside Indonesia the administrative fields are hidden, so requiring them would leave the
      // form permanently unsubmittable with errors on inputs the user cannot see.
      const required = isOtherCountry(addr.country)
        ? ADDRESS_REQUIRED_WHEN_PARTIAL(locale).filter(
            (f) => !INDONESIA_ONLY_ADDRESS_KEYS.includes(f.key as (typeof INDONESIA_ONLY_ADDRESS_KEYS)[number]),
          )
        : ADDRESS_REQUIRED_WHEN_PARTIAL(locale)

      const hasAny = required.some((f) => addr[f.key] != null && String(addr[f.key]).trim() !== '')
      if (hasAny) {
        for (const f of required) {
          if (addr[f.key] == null || String(addr[f.key]).trim() === '') {
            ctx.addIssue({ code: 'custom', message: f.msg, path: ['address', f.key] })
          }
        }
      }
    }
  })

export const OrganizationDetailsFormSchema = (locale: Locale) => OrganizationDetailsFormBaseSchema(locale)

export type OrganizationDetailsFormValues = z.infer<ReturnType<typeof OrganizationDetailsFormSchema>>

const ADDRESS_KEYS = ['addressLineOne', 'country', 'province', 'regency', 'district', 'village', 'postalCode'] as const

function formValuesEqual(current: OrganizationDetailsFormValues, initial: OrganizationDetailsFormValues): boolean {
  if (current.name !== initial.name) return false
  if (current.level !== initial.level) return false
  if (current.npsn !== initial.npsn) return false
  if (current.logo !== initial.logo) return false
  if (current.entityName !== initial.entityName) return false
  if (current.logoFile instanceof File) return false
  const curAddr = current.address
  const initAddr = initial.address
  for (const k of ADDRESS_KEYS) {
    const c = String(curAddr[k] ?? '').trim()
    const i = String(initAddr[k] ?? '').trim()
    if (c !== i) return false
  }
  return true
}

type ImageSelectorMode = 'upload' | 'avatar'

const LEVEL_OPTIONS = [
  { label: 'SD', value: 'SD' },
  { label: 'SMP', value: 'SMP' },
  { label: 'SMA', value: 'SMA' },
  { label: 'Semua', value: 'Semua' },
] as const

interface AddressData {
  label: string
  value: string
  /** Villages only: used to fill the postal code field when this option is picked. */
  postalCode?: string | null
}

interface OrganizationDetailsFormProps {
  onSubmit: (values: OrganizationDetailsFormValues) => Promise<void> | void
  initialValues?: Partial<OrganizationDetailsFormValues>
  submitButtonText?: string
  submitButtonClassName?: string
  /** Mode for image selection: 'upload' for file upload, 'avatar' for avatar selector (default: 'upload') */
  imageSelectorMode?: ImageSelectorMode
  /** Avatar data required when imageSelectorMode is 'avatar' */
  avatarData?: AvatarList[]
  /** Placeholder fallback for avatar selector */
  avatarPlaceholder?: string
  /** Show NPSN and Level fields (school-specific) */
  showSchoolDetails?: boolean
  /** Show entity name field (nama badan usaha) */
  showEntityName?: boolean
  /** Show address form fields */
  showAddressDetails?: boolean
  /** When true and showAddressDetails is true, address fields (province, regency, district, village) are required */
  requireAddressFields?: boolean
  /** Address data for dropdown selections (provinces, regencies, districts, villages) */
  addressData?: {
    provinces: AddressData[]
    regencies: AddressData[]
    districts: AddressData[]
    villages: AddressData[]
    countries: AddressData[]
    isLoadingProvinces?: boolean
    isLoadingRegencies?: boolean
    isLoadingDistricts?: boolean
    isLoadingVillages?: boolean
    isLoadingCountries?: boolean
  }
  /** Callbacks for address field changes to load dependent data */
  onAddressChange?: {
    onProvinceChange?: (province: string) => void
    onRegencyChange?: (regency: string) => void
    onDistrictChange?: (district: string) => void
    onVillageChange?: (village: string) => void
    onCountryChange?: (country: string) => void
  }
  /** Custom profile upload props */
  profileUploadClassName?: string
  profileUploadTitle?: string
  locale?: Locale
}

export function OrganizationDetailsForm({
  onSubmit,
  initialValues = {},
  submitButtonText = 'Perbarui',
  submitButtonClassName,
  imageSelectorMode = 'upload',
  avatarData = [],
  avatarPlaceholder = '?',
  showSchoolDetails = false,
  showEntityName = false,
  showAddressDetails = false,
  requireAddressFields = false,
  addressData = {
    provinces: [],
    regencies: [],
    districts: [],
    villages: [],
    countries: [],
  },
  onAddressChange = {},
  profileUploadClassName = 'srd:w-[60px] srd:h-[60px] srd:max-w-[60px] srd:max-h-[60px] srd:rounded-lg',
  profileUploadTitle = 'Ganti logo',
  locale = 'id',
}: OrganizationDetailsFormProps) {
  const validationSchema = requireAddressFields
    ? OrganizationDetailsFormSchemaWithAddressValidation(locale)
    : OrganizationDetailsFormSchema(locale)

  const initialFormValues = useMemo(
    (): OrganizationDetailsFormValues => ({
      name: initialValues.name ?? '',
      level: initialValues.level ?? null,
      npsn: initialValues.npsn ?? null,
      logo: initialValues.logo ?? null,
      logoFile: initialValues.logoFile ?? null,
      entityName: initialValues.entityName ?? null,
      address: initialValues.address ?? {},
    }),
    [
      initialValues.name,
      initialValues.level,
      initialValues.npsn,
      initialValues.logo,
      initialValues.logoFile,
      initialValues.entityName,
      initialValues.address,
    ],
  )

  const form = useForm({
    defaultValues: initialFormValues as z.input<ReturnType<typeof OrganizationDetailsFormSchema>>,
    validators: {
      onSubmit: validationSchema,
      onChange: validationSchema,
      // onBlur: validationSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, error] = await tryCatch(async () =>
        onSubmit({
          ...value,
          address: value.address ?? {},
        } as OrganizationDetailsFormValues),
      )

      if (error) {
        // @ts-expect-error - error.message is a string
        formApi.setErrorMap({ onSubmit: error.message })
      }
    },
  })

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const values = useStore(form.store, (state) => state.values) as OrganizationDetailsFormValues
  const address = useStore(form.store, (state) => state.values.address)
  const hasChanges = !formValuesEqual(values, initialFormValues)
  /** Outside Indonesia the street address is the only address input we can ask for. */
  const hideIndonesianAddressFields = isOtherCountry(address?.country)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldSet>
        <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-4'>
          {/* Logo/Image Upload */}
          <div className='srd:flex srd:w-full srd:items-center srd:justify-center'>
            {imageSelectorMode === 'upload' ? (
              <form.Field name='logoFile'>
                {(field) => (
                  <ProfileUpload
                    handleImageUploadAction={(file) => {
                      field.handleChange(file)
                    }}
                    locale={locale}
                    placeholderName={form.state.values.name ?? ''}
                    initialImageUrl={form.state.values.logo}
                    className={profileUploadClassName}
                    customTitle={profileUploadTitle}
                    profile='organization'
                  />
                )}
              </form.Field>
            ) : (
              <form.Field name='logo'>
                {(field) => (
                  <AvatarSelector
                    handleAvatarSelectAction={(avatar) => field.handleChange(avatar.src)}
                    avatarData={avatarData}
                    initialAvatarKey={
                      field.state.value ? (avatarData.find((a) => a.src === field.state.value)?.key ?? null) : null
                    }
                    placeholderFallback={avatarPlaceholder}
                    size={100}
                  />
                )}
              </form.Field>
            )}
          </div>

          {/* Organization Name */}
          <form.Field name='name'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    {showSchoolDetails
                      ? locale === 'id'
                        ? 'Nama Sekolah'
                        : 'School Name'
                      : locale === 'id'
                        ? 'Nama Organisasi'
                        : 'Organization Name'}
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          {/* Entity name (nama badan usaha) */}
          {showEntityName && (
            <form.Field name='entityName'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {locale === 'id' ? 'Nama Badan Usaha' : 'Business Name'}
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value ?? ''}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      aria-invalid={isInvalid}
                      placeholder={locale === 'id' ? 'PT. Contoh Nama Badan Usaha' : 'PT. Example Business Name'}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          )}

          {/* School-specific fields (NPSN, Level) */}
          {showSchoolDetails && (
            <div className='srd:grid srd:grid-cols-2 srd:gap-4'>
              <form.Field name='level'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Tingkatan' : 'Level'}</FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        items={LEVEL_OPTIONS}
                      >
                        <SelectTrigger id={field.name} aria-invalid={isInvalid} className='srd:min-w-[120px]'>
                          <SelectValue placeholder={locale === 'id' ? 'Pilih tingkatan' : 'Select level'} />
                        </SelectTrigger>
                        <SelectContent align='start'>
                          {LEVEL_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </form.Field>

              <form.Field name='npsn'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'NPSN' : 'NPSN'}</FieldLabel>
                      <Input
                        id={field.name}
                        name={field.name}
                        value={field.state.value ?? ''}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        aria-invalid={isInvalid}
                      />

                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </form.Field>
            </div>
          )}

          {/* Address fields */}
          {showAddressDetails && (
            <>
              {/* Address Line 1 - full width */}
              <form.Field name='address.addressLineOne'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Alamat' : 'Address'}</FieldLabel>
                      <Textarea
                        id={field.name}
                        name={field.name}
                        value={field.state.value ?? ''}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value || undefined)}
                        aria-invalid={isInvalid}
                        placeholder='Wisma Surya Kemang Lantai 2 Jl. Kemang Raya No. 33'
                      />
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </form.Field>

              {/* (Negara & Provinsi) | (Kota & Kecamatan) | (Kelurahan & Kode Pos) on md+; single column on mobile */}
              <div
                className={cn(
                  'srd:grid srd:w-full srd:grid-cols-1 srd:gap-4',
                  // Negara is the only selector left once the Indonesia-only fields go, so drop the
                  // second column rather than leaving a gap beside it.
                  !hideIndonesianAddressFields && 'srd:md:grid-cols-2',
                )}
              >
                {/* Negara */}
                <form.Field name='address.country'>
                  {(field) => {
                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                    const selectedCountry = addressData.countries.find((c) => c.value === field.state.value) ?? null
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Negara' : 'Country'}</FieldLabel>
                        <Combobox<AddressData>
                          items={addressData.countries}
                          value={selectedCountry}
                          onValueChange={(item) => {
                            if (item) {
                              field.handleChange(item.value)
                              // The Indonesia-only inputs disappear for other countries, so clear
                              // them — otherwise stale values submit from behind hidden fields.
                              if (isOtherCountry(item.value)) {
                                form.setFieldValue('address.province', null)
                                form.setFieldValue('address.regency', null)
                                form.setFieldValue('address.district', null)
                                form.setFieldValue('address.village', null)
                                form.setFieldValue('address.postalCode', null)
                              }
                              onAddressChange.onCountryChange?.(item.value)
                            }
                          }}
                          disabled={addressData.isLoadingCountries}
                          isItemEqualToValue={(a, b) => a.value === b.value}
                        >
                          <ComboboxTrigger
                            render={
                              <Button
                                type='button'
                                variant='outline'
                                baseButton
                                id={field.name}
                                aria-invalid={isInvalid}
                                className={ADDRESS_TRIGGER_CLASSNAME}
                              >
                                <ComboboxValue>
                                  {(item: AddressData | null) => (
                                    <span
                                      className={cn(
                                        'srd:block srd:w-full srd:truncate srd:text-left',
                                        !item ? 'srd:text-grey-60' : 'srd:text-neutral-black',
                                      )}
                                    >
                                      {item ? item.label : locale === 'id' ? 'Pilih negara' : 'Select country'}
                                    </span>
                                  )}
                                </ComboboxValue>
                              </Button>
                            }
                          />
                          <ComboboxContent align='start'>
                            <ComboboxInput
                              showTrigger={false}
                              placeholder={locale === 'id' ? 'Cari negara...' : 'Search country...'}
                            />
                            <ComboboxEmpty>
                              {locale === 'id' ? 'Tidak ada negara ditemukan.' : 'No country found.'}
                            </ComboboxEmpty>
                            <ComboboxList>
                              {(item: AddressData) => (
                                <ComboboxItem key={item.value} value={item}>
                                  {item.label}
                                </ComboboxItem>
                              )}
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                        {isInvalid && <FieldError errors={field.state.meta.errors} />}
                      </Field>
                    )
                  }}
                </form.Field>

                {!hideIndonesianAddressFields && (
                  <>
                    {/* Provinsi */}
                    <form.Field name='address.province'>
                      {(field) => {
                        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                        const selectedProvince =
                          addressData.provinces.find((p) => p.value === field.state.value) ?? null
                        const countrySelected = address?.country != null && String(address.country).trim() !== ''
                        const placeholder = countrySelected
                          ? locale === 'id'
                            ? 'Pilih provinsi'
                            : 'Select province'
                          : locale === 'id'
                            ? 'Pilih negara terlebih dahulu'
                            : 'Select country first'
                        return (
                          <Field data-invalid={isInvalid}>
                            <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Provinsi' : 'Province'}</FieldLabel>
                            <Combobox<AddressData>
                              items={addressData.provinces}
                              value={selectedProvince}
                              onValueChange={(item) => {
                                if (item) {
                                  field.handleChange(item.value)
                                  onAddressChange.onProvinceChange?.(item.value)
                                }
                              }}
                              disabled={!countrySelected || addressData.isLoadingProvinces}
                              isItemEqualToValue={(a, b) => a.value === b.value}
                            >
                              <ComboboxTrigger
                                render={
                                  <Button
                                    type='button'
                                    variant='outline'
                                    baseButton
                                    id={field.name}
                                    aria-invalid={isInvalid}
                                    className={ADDRESS_TRIGGER_CLASSNAME}
                                  >
                                    <ComboboxValue>
                                      {(item: AddressData | null) => (
                                        <span
                                          className={cn(
                                            !item ? 'srd:text-neutrals-n-50' : 'srd:text-complementary-black',
                                          )}
                                        >
                                          {item ? item.label : placeholder}
                                        </span>
                                      )}
                                    </ComboboxValue>
                                  </Button>
                                }
                              />
                              <ComboboxContent align='start'>
                                <ComboboxInput
                                  showTrigger={false}
                                  placeholder={locale === 'id' ? 'Cari provinsi...' : 'Search province...'}
                                />
                                <ComboboxEmpty>
                                  {locale === 'id' ? 'Tidak ada provinsi ditemukan.' : 'No province found.'}
                                </ComboboxEmpty>
                                <ComboboxList>
                                  {(item: AddressData) => (
                                    <ComboboxItem key={item.value} value={item}>
                                      {item.label}
                                    </ComboboxItem>
                                  )}
                                </ComboboxList>
                              </ComboboxContent>
                            </Combobox>
                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                          </Field>
                        )
                      }}
                    </form.Field>

                    {/* Kota */}
                    <form.Field name='address.regency'>
                      {(field) => {
                        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                        const selectedRegency = addressData.regencies.find((r) => r.value === field.state.value) ?? null
                        const provinceSelected = address?.province != null && String(address.province).trim() !== ''
                        const placeholder = provinceSelected
                          ? locale === 'id'
                            ? 'Pilih kota'
                            : 'Select city'
                          : locale === 'id'
                            ? 'Pilih provinsi terlebih dahulu'
                            : 'Select province first'

                        return (
                          <Field data-invalid={isInvalid}>
                            <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Kota' : 'City'}</FieldLabel>
                            <Combobox<AddressData>
                              items={addressData.regencies}
                              value={selectedRegency}
                              onValueChange={(item) => {
                                if (item) {
                                  field.handleChange(item.value)
                                  onAddressChange.onRegencyChange?.(item.value)
                                }
                              }}
                              disabled={!provinceSelected || addressData.isLoadingRegencies}
                              isItemEqualToValue={(a, b) => a.value === b.value}
                            >
                              <ComboboxTrigger
                                render={
                                  <Button
                                    type='button'
                                    variant='outline'
                                    baseButton
                                    id={field.name}
                                    aria-invalid={isInvalid}
                                    className={ADDRESS_TRIGGER_CLASSNAME}
                                  >
                                    <ComboboxValue>
                                      {(item: AddressData | null) => (
                                        <span
                                          className={cn(
                                            !item ? 'srd:text-neutrals-n-50' : 'srd:text-complementary-black',
                                          )}
                                        >
                                          {item ? item.label : placeholder}
                                        </span>
                                      )}
                                    </ComboboxValue>
                                  </Button>
                                }
                              />
                              <ComboboxContent align='start'>
                                <ComboboxInput
                                  showTrigger={false}
                                  placeholder={locale === 'id' ? 'Cari kota...' : 'Search city...'}
                                />
                                <ComboboxEmpty>
                                  {locale === 'id' ? 'Tidak ada kota ditemukan.' : 'No city found.'}
                                </ComboboxEmpty>
                                <ComboboxList>
                                  {(item: AddressData) => (
                                    <ComboboxItem key={item.value} value={item}>
                                      {item.label}
                                    </ComboboxItem>
                                  )}
                                </ComboboxList>
                              </ComboboxContent>
                            </Combobox>
                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                          </Field>
                        )
                      }}
                    </form.Field>

                    {/* Kecamatan */}
                    <form.Field name='address.district'>
                      {(field) => {
                        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                        const selectedDistrict =
                          addressData.districts.find((d) => d.value === field.state.value) ?? null
                        const regencySelected = address?.regency != null && String(address.regency).trim() !== ''
                        const placeholder = regencySelected ? 'Pilih kecamatan' : 'Pilih kota terlebih dahulu'
                        return (
                          <Field data-invalid={isInvalid}>
                            <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Kecamatan' : 'District'}</FieldLabel>
                            <Combobox<AddressData>
                              items={addressData.districts}
                              value={selectedDistrict}
                              onValueChange={(item) => {
                                if (item) {
                                  field.handleChange(item.value)
                                  onAddressChange.onDistrictChange?.(item.value)
                                }
                              }}
                              disabled={!regencySelected || addressData.isLoadingDistricts}
                              isItemEqualToValue={(a, b) => a.value === b.value}
                            >
                              <ComboboxTrigger
                                render={
                                  <Button
                                    type='button'
                                    variant='outline'
                                    baseButton
                                    id={field.name}
                                    aria-invalid={isInvalid}
                                    className={ADDRESS_TRIGGER_CLASSNAME}
                                  >
                                    <ComboboxValue>
                                      {(item: AddressData | null) => (
                                        <span
                                          className={cn(
                                            !item ? 'srd:text-neutrals-n-50' : 'srd:text-complementary-black',
                                          )}
                                        >
                                          {item ? item.label : placeholder}
                                        </span>
                                      )}
                                    </ComboboxValue>
                                  </Button>
                                }
                              />
                              <ComboboxContent align='start'>
                                <ComboboxInput
                                  showTrigger={false}
                                  placeholder={locale === 'id' ? 'Cari kecamatan...' : 'Search district...'}
                                />
                                <ComboboxEmpty>
                                  {locale === 'id' ? 'Tidak ada kecamatan ditemukan.' : 'No district found.'}
                                </ComboboxEmpty>
                                <ComboboxList>
                                  {(item: AddressData) => (
                                    <ComboboxItem key={item.value} value={item}>
                                      {item.label}
                                    </ComboboxItem>
                                  )}
                                </ComboboxList>
                              </ComboboxContent>
                            </Combobox>
                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                          </Field>
                        )
                      }}
                    </form.Field>

                    {/* Kelurahan */}
                    <form.Field name='address.village'>
                      {(field) => {
                        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                        const selectedVillage = addressData.villages.find((v) => v.value === field.state.value) ?? null
                        const districtSelected = address?.district != null && String(address.district).trim() !== ''
                        const placeholder = districtSelected
                          ? locale === 'id'
                            ? 'Pilih kelurahan'
                            : 'Select village'
                          : locale === 'id'
                            ? 'Pilih kecamatan terlebih dahulu'
                            : 'Select district first'

                        return (
                          <Field data-invalid={isInvalid}>
                            <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Kelurahan' : 'Village'}</FieldLabel>
                            <Combobox<AddressData>
                              items={addressData.villages}
                              value={selectedVillage}
                              onValueChange={(item) => {
                                if (item) {
                                  field.handleChange(item.value)
                                  // The village determines the postal code, so a new village always
                                  // wins over whatever was in the field before.
                                  if (item.postalCode) form.setFieldValue('address.postalCode', item.postalCode)
                                  onAddressChange.onVillageChange?.(item.value)
                                }
                              }}
                              disabled={!districtSelected || addressData.isLoadingVillages}
                              isItemEqualToValue={(a, b) => a.value === b.value}
                            >
                              <ComboboxTrigger
                                render={
                                  <Button
                                    type='button'
                                    variant='outline'
                                    baseButton
                                    id={field.name}
                                    aria-invalid={isInvalid}
                                    className={ADDRESS_TRIGGER_CLASSNAME}
                                  >
                                    <ComboboxValue>
                                      {(item: AddressData | null) => (
                                        <span
                                          className={cn(
                                            !item ? 'srd:text-neutrals-n-50' : 'srd:text-complementary-black',
                                          )}
                                        >
                                          {item ? item.label : placeholder}
                                        </span>
                                      )}
                                    </ComboboxValue>
                                  </Button>
                                }
                              />
                              <ComboboxContent align='start'>
                                <ComboboxInput
                                  showTrigger={false}
                                  placeholder={locale === 'id' ? 'Cari kelurahan...' : 'Search village...'}
                                />
                                <ComboboxEmpty>
                                  {locale === 'id' ? 'Tidak ada kelurahan ditemukan.' : 'No village found.'}
                                </ComboboxEmpty>
                                <ComboboxList>
                                  {(item: AddressData) => (
                                    <ComboboxItem key={item.value} value={item}>
                                      {item.label}
                                    </ComboboxItem>
                                  )}
                                </ComboboxList>
                              </ComboboxContent>
                            </Combobox>
                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                          </Field>
                        )
                      }}
                    </form.Field>

                    {/* Kode Pos */}
                    <form.Field name='address.postalCode'>
                      {(field) => {
                        const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                        return (
                          <Field data-invalid={isInvalid}>
                            <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Kode Pos' : 'Postal Code'}</FieldLabel>
                            <Input
                              id={field.name}
                              name={field.name}
                              value={field.state.value ?? ''}
                              onBlur={field.handleBlur}
                              onChange={(e) => field.handleChange(e.target.value || null)}
                              aria-invalid={isInvalid}
                            />
                            {isInvalid && <FieldError errors={field.state.meta.errors} />}
                          </Field>
                        )
                      }}
                    </form.Field>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </FieldSet>

      {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
        <p className='srd:text-main-red-100 srd:mt-4 srd:text-xs'>{errorMap.onSubmit}</p>
      ) : null}

      <Button
        type='submit'
        // Only gated on "nothing changed". Gating on validity too left the button dead with no
        // explanation, because field errors stay hidden until a field is touched or submitted.
        disabled={!hasChanges}
        loading={isSubmitting}
        className={cn('srd:bg-main-blue-100 srd:mt-8 srd:w-full srd:rounded-full', submitButtonClassName)}
      >
        {submitButtonText}
      </Button>
    </form>
  )
}
